/**
 * Pure volumetrics derivation for the AI Token Cost Calculator.
 *
 * Given any ONE entered volumetrics field (perDay, perMonth, or perYear),
 * the daily figure is treated as the base rate and the monthly/yearly totals
 * are the daily rate scaled by the configurable calendar basis:
 *
 *   perMonth = perDay × daysPerMonth   (default 365/12 ≈ 30.42)
 *   perYear  = perDay × daysPerYear    (default 365)
 *
 * and the inverse direction when a coarser period is entered:
 *
 *   perDay (from perMonth) = round(perMonth / daysPerMonth)
 *   perDay (from perYear)  = round(perYear  / daysPerYear)
 *
 * The entered field is treated as authoritative and is preserved exactly as
 * entered; `perMonth` is the canonical period used for monthly cost. Every
 * derived value is rounded to the nearest whole number.
 *
 * This is a pure function with no side effects, shared by both the server
 * (@tcc/server) and the client (@tcc/client).
 */
/** Legacy working-days-per-month basis (kept for reference; no longer the default). */
export const WORKING_DAYS_PER_MONTH = 21;
/** Months per year (kept for reference/back-compat). */
export const MONTHS_PER_YEAR = 12;
/** Default days-per-month conversion basis: calendar-average month (365/12 ≈ 30.42). */
export const DEFAULT_DAYS_PER_MONTH = 365 / 12;
/** Default days-per-year conversion basis: a full calendar year. */
export const DEFAULT_DAYS_PER_YEAR = 365;
/**
 * Derive the full set of volumetrics from a single entered field.
 *
 * @param enteredField Which field the user entered (authoritative).
 * @param value The whole-number value entered for that field.
 * @param daysPerMonth Days-per-month conversion basis (default 365/12). Values
 *   ≤ 0 fall back to the default.
 * @param daysPerYear Days-per-year conversion basis (default 365). Values ≤ 0
 *   fall back to the default.
 * @returns The perDay, perMonth, and perYear values, with `enteredField`
 *   preserved exactly and the other two derived and rounded to whole numbers.
 */
export function deriveVolumetrics(enteredField, value, daysPerMonth = DEFAULT_DAYS_PER_MONTH, daysPerYear = DEFAULT_DAYS_PER_YEAR) {
    const dpm = daysPerMonth > 0 ? daysPerMonth : DEFAULT_DAYS_PER_MONTH;
    const dpy = daysPerYear > 0 ? daysPerYear : DEFAULT_DAYS_PER_YEAR;
    switch (enteredField) {
        case 'perDay': {
            const perDay = value;
            return { perDay, perMonth: Math.round(perDay * dpm), perYear: Math.round(perDay * dpy) };
        }
        case 'perMonth': {
            const perMonth = value;
            const perDay = Math.round(perMonth / dpm);
            return { perDay, perMonth, perYear: Math.round(perDay * dpy) };
        }
        case 'perYear': {
            const perYear = value;
            const perDay = Math.round(perYear / dpy);
            return { perDay, perMonth: Math.round(perDay * dpm), perYear };
        }
    }
}
//# sourceMappingURL=volumetrics.js.map